import { onMounted } from "vue";
import { onFullscreenChange } from "@/viewer/utils.js";
import { CSS_CLASSES } from "@/viewer/constants.js";

/**
 * Vue Composable: Viewer Event Management
 *
 * This composable centralizes all event handling logic for the IIIF viewer.
 * It manages keyboard shortcuts, mouse interactions, and fullscreen state changes
 * with proper cleanup and cross-browser compatibility.
 *
 * Event Types Handled:
 * - Keyboard: Escape key for modal closing
 * - Mouse: Click-outside behavior for modal dismissal
 * - Fullscreen: Cross-browser fullscreen state changes
 *
 * @param {Function} closeToPreview - Callback to close modal and return to preview
 * @param {import('vue').ComputedRef<boolean>} isFullscreenState - Reactive fullscreen state
 * @returns {Object} Event handlers and setup functions
 */
export function useViewerEvents(closeToPreview, isFullscreenState) {
    // KEYBOARD EVENT HANDLERS

    /**
     * Handle escape key and modal overlay clicks
     *
     * Provides multiple ways to close the modal for better UX:
     * - Escape key: Standard modal behavior
     * - Overlay click: Click on modal backdrop to close
     *
     * @param {KeyboardEvent} event - Keyboard event object
     * @param {import('vue').Ref} modalRef - Reference to modal overlay element
     */
    function handleEscape(event, modalRef) {
        if (event.key === "Escape" || event.target === modalRef?.value) {
            closeToPreview();
        }
    }

    // MOUSE EVENT HANDLERS

    /**
     * Handle body click events for modal closing
     *
     * Implements click-outside behavior when in modal state. Only active when
     * the iframe-partial-fullscreen class is present, ensuring this doesn't
     * interfere with normal preview interactions.
     *
     * @param {MouseEvent} event - Mouse click event object
     */
    function handleBodyClick(event) {
        if (
            !document.body.classList.contains(
                CSS_CLASSES.IFRAME_PARTIAL_FULLSCREEN
            )
        ) {
            return;
        }
        if (event.target === document.body) {
            closeToPreview();
        }
    }

    // FULLSCREEN EVENT HANDLERS

    /**
     * Handle fullscreen state changes
     *
     * Automatically closes the modal when the user exits fullscreen using
     * browser controls (F11, Escape in fullscreen, etc.). This ensures the
     * viewer state stays consistent with the actual browser fullscreen state.
     *
     * @param {boolean} fullscreen - True if currently in fullscreen mode
     */
    function handleFullscreenChange(fullscreen) {
        if (!fullscreen && isFullscreenState.value) {
            closeToPreview();
        }
    }

    // EVENT LISTENER SETUP

    /**
     * Setup all event listeners on component mount
     *
     * Initializes all event listeners when the component mounts. This ensures
     * proper timing and avoids trying to attach listeners before the DOM is ready.
     * Uses Vue's onMounted to integrate with the component lifecycle.
     *
     * Event Listeners Added:
     * - Cross-browser fullscreen change events
     * - Global keydown events for escape handling
     * - Document body click events for modal dismissal
     *
     * @param {import('vue').Ref} modalRef - Reference to modal overlay element
     */
    function setupEventListeners(modalRef) {
        onMounted(() => {
            // Cross-browser fullscreen change detection
            onFullscreenChange(handleFullscreenChange);

            // Global keyboard event handling
            document.addEventListener("keydown", (event) =>
                handleEscape(event, modalRef)
            );

            // Global mouse event handling
            document.body.addEventListener("click", handleBodyClick);
        });
    }

    // PUBLIC API

    return {
        // Individual Event Handlers
        handleEscape,
        handleBodyClick,
        handleFullscreenChange,

        // Lifecycle Management
        setupEventListeners,
    };
}
